import { getIdToken, signOut } from './auth';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Session is gone — clear Cognito state and send the user back to the app root.
 *
 * Deliberately targets '/' rather than '/login': this is a full page load, and
 * the static host only serves index.html for the root path, so requesting a
 * deep path directly returns a 404. From the root the router lands the
 * signed-out user on /login anyway.
 */
function redirectToLogin(): void {
  signOut();
  window.location.assign('/');
}

/**
 * Thin wrapper around fetch for the Topzite API.
 * - Adds Authorization header from Cognito session
 * - Parses JSON responses
 * - Throws on non-2xx with error message
 * - Redirects to /login when the session has expired
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await getIdToken();
  if (!token) {
    redirectToLogin();
    throw new Error('Your session has expired. Please sign in again.');
  }

  const url = `${API_URL}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(options.headers as Record<string, string> || {}),
  };

  const res = await fetch(url, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    redirectToLogin();
    throw new Error('Your session has expired. Please sign in again.');
  }

  // Handle empty responses (204, etc.) and non-JSON error bodies
  // (e.g. plain-text 502/503 pages from API Gateway)
  const text = await res.text();
  let data: { error?: string; data?: unknown } = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (!res.ok) {
    throw new Error(data.error || `Something went wrong (error ${res.status}). Please try again.`);
  }

  return data.data as T;
}

// ─── Sites ───────────────────────────────────────────────────────────

import type { Site, ContentItem, MediaItem, UploadResponse, TemplateManifest } from '../types';

export const api = {
  // Sites
  listSites: () => request<Site[]>('/sites'),

  createSite: (siteName: string, templateId: string) =>
    request<Site>('/sites', {
      method: 'POST',
      body: JSON.stringify({ siteName, templateId }),
    }),

  getSite: (siteId: string) => request<Site>(`/sites/${siteId}`),

  updateSite: (siteId: string, data: { siteName?: string; templateId?: string }) =>
    request<Site>(`/sites/${siteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteSite: (siteId: string) =>
    request<{ message: string }>(`/sites/${siteId}`, { method: 'DELETE' }),

  setDomain: (siteId: string, customDomain: string) =>
    request<Site>(`/sites/${siteId}/domain`, {
      method: 'PUT',
      body: JSON.stringify({ customDomain }),
    }),

  getDomainStatus: (siteId: string) =>
    request<{
      domainStatus: string;
      customDomain?: string;
      acmValidationCname?: { name: string; value: string } | null;
      cloudfrontDomainName?: string;
      message?: string;
    }>(`/sites/${siteId}/domain/status`),

  removeCustomDomain: (siteId: string) =>
    request<{ domainStatus: string; message: string }>(`/sites/${siteId}/domain`, {
      method: 'DELETE',
    }),

  // Deploy
  deploy: (siteId: string) =>
    request<{ status: string; message: string }>(`/sites/${siteId}/deploy`, {
      method: 'POST',
    }),

  triggerPreview: (siteId: string) =>
    request<{ status: string; previewUrl: string; message: string }>(`/sites/${siteId}/deploy/preview`, {
      method: 'POST',
    }),

  getDeployPreview: (siteId: string) =>
    request<{
      lastDeployedAt: string | null;
      neverDeployed: boolean;
      changes: {
        created: Array<{ type: string; title: string; contentKey: string }>;
        modified: Array<{ type: string; title: string; contentKey: string }>;
        summary: string;
      };
    }>(`/sites/${siteId}/deploy/preview`),

  // Analytics
  getAnalytics: (siteId: string, period: string = '30d') =>
    request<{
      period: string;
      totalVisits: number;
      totalRequests: number;
      totalBandwidth: number;
      timeseries: Array<{ date: string; visits: number; requests: number; bandwidth: number }>;
      topPages: Array<{ path: string; requests: number }>;
      topCountries: Array<{ country: string; requests: number }>;
    }>(`/sites/${siteId}/analytics?period=${period}`),

  // Content
  listAllContent: (siteId: string) =>
    request<ContentItem[]>(`/sites/${siteId}/content`),

  listContent: (siteId: string, type: string) =>
    request<ContentItem[]>(`/sites/${siteId}/content/${type}`),

  createContent: (siteId: string, type: string, data: Record<string, unknown>, sortOrder?: number) =>
    request<ContentItem>(`/sites/${siteId}/content/${type}`, {
      method: 'POST',
      body: JSON.stringify({ data, sortOrder: sortOrder ?? 0 }),
    }),

  updateContent: (
    siteId: string,
    type: string,
    id: string,
    data: Record<string, unknown>,
    sortOrder?: number,
  ) =>
    request<ContentItem>(`/sites/${siteId}/content/${type}/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ data, ...(sortOrder !== undefined ? { sortOrder } : {}) }),
    }),

  deleteContent: (siteId: string, type: string, id: string) =>
    request<{ message: string }>(`/sites/${siteId}/content/${type}/${id}`, {
      method: 'DELETE',
    }),

  reorderContent: (siteId: string, type: string, items: { id: string; sortOrder: number }[]) =>
    request<{ message: string }>(`/sites/${siteId}/content/${type}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    }),

  // Media
  getUploadUrl: (siteId: string, filename: string, contentType: string) =>
    request<UploadResponse>(`/sites/${siteId}/media/upload`, {
      method: 'POST',
      body: JSON.stringify({ filename, contentType }),
    }),

  listMedia: (siteId: string) =>
    request<MediaItem[]>(`/sites/${siteId}/media`),

  deleteMedia: (siteId: string, mediaId: string) =>
    request<{ message: string }>(`/sites/${siteId}/media/${mediaId}`, {
      method: 'DELETE',
    }),

  getStorageUsage: (siteId: string) =>
    request<{ usedBytes: number; usedMb: number; limitMb: number; percentUsed: number }>(
      `/sites/${siteId}/media/usage`,
    ),

  // Versions
  listVersions: (siteId: string) =>
    request<Array<{ versionId: string; createdAt: string; contentCount: number; snapshotSizeBytes: number; status: string }>>(
      `/sites/${siteId}/versions`,
    ),

  restoreVersion: (siteId: string, versionId: string) =>
    request<{ restoredCount: number }>(`/sites/${siteId}/versions/${versionId}/restore`, {
      method: 'POST',
    }),

  // Billing
  getBillingPlan: () =>
    request<{
      plan: 'trial' | 'friend' | 'active';
      trialExpiresAt: string | null;
      siteLimit: number | null;
      storageGBPurchased: number;
      hasStripe: boolean;
    }>('/stripe/plan'),

  createCheckoutSession: () =>
    request<{ url: string }>('/stripe/checkout', { method: 'POST' }),

  createPortalSession: () =>
    request<{ url: string }>('/stripe/portal', { method: 'POST' }),

  addStorage: (gb: number) =>
    request<{ storageGBPurchased: number }>('/stripe/storage', {
      method: 'POST',
      body: JSON.stringify({ gb }),
    }),

  // Template manifest
  getTemplateManifest: (templateId: string) =>
    request<TemplateManifest>(`/templates/${templateId}/manifest`),

  // Template versions
  getTemplateVersions: (templateId: string) =>
    request<{ templateId: string; versions: string[]; latest: string | null }>(
      `/templates/${templateId}/versions`,
    ),

  updateTemplateVersion: (siteId: string, templateVersion: string) =>
    request<Site>(`/sites/${siteId}`, {
      method: 'PUT',
      body: JSON.stringify({ templateVersion }),
    }),
};

/**
 * Upload a file to S3 using a presigned URL.
 * Returns the S3 key for the uploaded file.
 */
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB

export async function uploadFile(
  siteId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<UploadResponse> {
  // Client-side size check
  if (file.size > MAX_UPLOAD_SIZE) {
    throw new Error(`File too large. Maximum size is ${MAX_UPLOAD_SIZE / 1024 / 1024}MB.`);
  }
  // 1. Get presigned URL from our API
  const upload = await api.getUploadUrl(siteId, file.name, file.type);

  // 2. Upload directly to S3
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', upload.uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.send(file);
  });

  return upload;
}
